import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {Moment} from 'moment-timezone'

import type {LatestPeakLoadWindow} from '../api'

export const PEAK_LOAD_WINDOWS_QUERY_KEY = 'peakLoadWindows'

type MomentRange = [start: Moment, end: Moment]

export const peakLoadWindowsQueryOptions = (plantId: string, timeFrame: MomentRange) => {
  const [from, to] = timeFrame
  return {
    queryKey: [
      PEAK_LOAD_WINDOWS_QUERY_KEY,
      {plantCode: plantId, from: from.toISOString(), to: to.toISOString()}
    ] as const,
    queryFn: async ({signal}: {signal?: AbortSignal}) => {
      const response = await ApiClient.axiosInstance.get<LatestPeakLoadWindow[]>(
        `/hcem/ui/peak_load_windows/${plantId}`,
        {
          params: {time_from: from.toISOString(), time_to: to.toISOString()},
          signal
        }
      )
      return response.data
    }
  }
}

interface UsePeakLoadWindowsQueryParams {
  plantId: string
  timeFrame: MomentRange
  enabled?: boolean
  throwOnError?: boolean
}

type PeakLoadWindowsQueryOptions<TSelect> = Omit<
  UseQueryOptions<LatestPeakLoadWindow[], AxiosError, TSelect>,
  'queryKey' | 'queryFn'
>

export const usePeakLoadWindowsQuery = <TSelect = LatestPeakLoadWindow[]>({
  plantId,
  timeFrame,
  enabled = true,
  throwOnError = true,
  ...options
}: UsePeakLoadWindowsQueryParams &
  Omit<PeakLoadWindowsQueryOptions<TSelect>, 'enabled' | 'throwOnError'>) => {
  return useQuery<LatestPeakLoadWindow[], AxiosError, TSelect>({
    ...peakLoadWindowsQueryOptions(plantId, timeFrame),
    throwOnError,
    enabled,
    ...(enabled ? {} : {gcTime: 0}),
    ...options
  })
}
